import { Button } from '../../Utility/Button';
import Input from '../../Utility/FormElements/Input';
import AdminContainer from '../../Utility/AdminContainer';
import { DLPState } from '../../../Types/Enums';
import AdminBox from '../../Utility/AdminBox';
import ToggleSwitch from '../../Utility/ToggleSwitch';
import ImageUpload from '../../Utility/ImageUpload';
import AdminBoxHeadline from '../../Utility/AdminBoxHeadline';
import useEmbeddingModel from '../../../Hooks/useEmbeddingModel';
import Select from '../../Utility/FormElements/Select';

function EmbeddingModel() {
    const {
        EmbeddingModelActions,
        embeddingModelSnapshot,
        embeddingModel,
        imagePreview,
        icapAddress,
        i18n,
        loadingState,
        embeddingModelIdentifier,
        credentials,
    } = useEmbeddingModel();

    const handleSave = () => {
        EmbeddingModelActions.handleSave();
    };

    return (
        <div className={`admin-container flex relative flex-col`}>
            <AdminContainer>
                <AdminBox contentLoaded={loadingState.modelLoaded && loadingState.credentialsLoaded}>
                    <div className="flex flex-col gap-5">
                        <AdminBoxHeadline
                            headLine={i18n.translate({
                                id: embeddingModelIdentifier === 'new' ? 'newModel' : 'editModel',
                                sourceName: 'EmbeddingModel',
                            })}
                            subLine={embeddingModelIdentifier === 'new' ? undefined : embeddingModelSnapshot?.title}
                        />

                        <ImageUpload
                            handleImageChange={(e) => {
                                EmbeddingModelActions.handleImageChange(e);
                            }}
                            camRemoveImage={false}
                            imagePreview={imagePreview}
                        >
                            <Input
                                inputType="text"
                                value={embeddingModel.title ?? ''}
                                onChange={(value) => EmbeddingModelActions.handleChangeModel('title', value)}
                                placeholder={i18n.translate({ id: 'placeholder.title', sourceName: 'EmbeddingModel' })}
                                className="w-full"
                            />
                        </ImageUpload>

                        <div>
                            <label className="block mb-2">
                                {i18n.translate({ id: 'title.modelName', sourceName: 'EmbeddingModel' })}
                            </label>
                            <Input
                                inputType="text"
                                value={embeddingModel.value ?? ''}
                                onChange={(value) => EmbeddingModelActions.handleChangeModel('value', value)}
                                placeholder={i18n.translate({
                                    id: 'placeholder.modelName',
                                    sourceName: 'EmbeddingModel',
                                })}
                            />
                        </div>

                        <div>
                            <label className="block mb-2">
                                {i18n.translate({ id: 'title.credentials', sourceName: 'Model' })}
                            </label>
                            <Select
                                value={embeddingModel.credential?.identifier ?? '---'}
                                onChange={EmbeddingModelActions.handleCredentialDropdownChange}
                            >
                                <option key="---" value="---">
                                    {i18n.translate({ id: 'option', sourceName: 'Main' })}
                                </option>
                                {credentials.map((credential) => (
                                    <option key={credential.identifier} value={credential.identifier}>
                                        {credential.name}
                                    </option>
                                ))}
                            </Select>
                        </div>

                        <div>
                            <label className="block mb-2">
                                {i18n.translate({
                                    id: 'title.costOfOutputTokens',
                                    sourceName: 'Model',
                                })}
                            </label>
                            <Input
                                inputType="number"
                                value={embeddingModel.costs ?? 0}
                                onChange={(value) => EmbeddingModelActions.handleChangeModel('costs', value)}
                                inputProps={{ step: 0.01 }}
                            />
                        </div>

                        <div className="flex flex-col lg:flex-row gap-5">
                            <div className="flex-1">
                                <h5 className="mt-5">
                                    {i18n.translate({ id: 'basis.dlp.title', sourceName: 'EmbeddingModel' })}
                                </h5>
                                <h6>{i18n.translate({ id: 'gdpr.dlp.title', sourceName: 'EmbeddingModel' })}</h6>
                                <p>{i18n.translate({ id: 'gdpr.dlp.text', sourceName: 'EmbeddingModel' })}</p>
                                <div className="mt-5">
                                    <ToggleSwitch
                                        options={[
                                            {
                                                label: i18n.translate({ id: 'dlp.on', sourceName: 'Model' }),
                                                function: () =>
                                                    EmbeddingModelActions.handleChangeModel('dlp', DLPState.dlp_on),
                                                active: embeddingModel.dlp === DLPState.dlp_on,
                                            },
                                            {
                                                label: i18n.translate({ id: 'dlp.optional', sourceName: 'Model' }),
                                                function: () =>
                                                    EmbeddingModelActions.handleChangeModel(
                                                        'dlp',
                                                        DLPState.dlp_optional,
                                                    ),
                                                active: embeddingModel.dlp === DLPState.dlp_optional,
                                            },
                                            {
                                                label: i18n.translate({ id: 'dlp.off', sourceName: 'Model' }),
                                                function: () =>
                                                    EmbeddingModelActions.handleChangeModel('dlp', DLPState.dlp_off),
                                                active: embeddingModel.dlp === DLPState.dlp_off,
                                            },
                                        ]}
                                    />
                                </div>
                            </div>
                            <div
                                className={`flex-1 flex flex-col justify-between ${
                                    icapAddress ? '' : 'opacity-50 pointer-events-none'
                                }`}
                            >
                                <div>
                                    <h5 className="mt-5">
                                        {i18n.translate({
                                            id: 'enterprise.dlp.title',
                                            sourceName: 'EmbeddingModel',
                                        })}
                                    </h5>
                                    <h6>
                                        {i18n.translate({
                                            id: 'icap.dlp.title',
                                            sourceName: 'EmbeddingModel',
                                        })}
                                    </h6>
                                    <p>
                                        {i18n.translate({
                                            id: 'icap.dlp.text',
                                            sourceName: 'EmbeddingModel',
                                        })}
                                    </p>
                                </div>
                                <div className="mt-5 ">
                                    <ToggleSwitch
                                        options={[
                                            {
                                                label: i18n.translate({ id: 'dlp.on', sourceName: 'EmbeddingModel' }),
                                                function: () =>
                                                    EmbeddingModelActions.handleChangeModel('icapDlpActive', true),
                                                active: embeddingModel.icapDlpActive,
                                            },
                                            {
                                                label: i18n.translate({
                                                    id: 'dlp.off',
                                                    sourceName: 'EmbeddingModel',
                                                }),
                                                function: () =>
                                                    EmbeddingModelActions.handleChangeModel('icapDlpActive', false),
                                                active: !embeddingModel.icapDlpActive,
                                            },
                                        ]}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="mt-5 text-right">
                            <Button variant={'primary'} onClick={handleSave}>
                                {i18n.translate({ id: 'save', sourceName: 'Main' })}
                            </Button>
                        </div>
                    </div>
                </AdminBox>
            </AdminContainer>
        </div>
    );
}

export default EmbeddingModel;
